import {useEffect, useState} from 'react';
import {ChevronDown, ChevronRight, History, Loader2, RotateCcw} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {toast} from '@common-web/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@common-web/components/ui/dialog';
import {diffGroupConfigs} from '@common/enrollmentSite/configDiff';
import type {GroupConfig} from '@common/enrollmentSite/types';
import {ConfigDiffList} from './ConfigDiffList';

interface RestoreResult {
  siteConfig: unknown;
  e123Config: unknown;
  version: number;
}

interface VersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupSlug: string;
  canManage: boolean;
  // Restoring overwrites the editor draft — confirm inline only when there are unsaved edits to lose.
  dirty: boolean;
  onRestored: (res: RestoreResult) => void;
}

function formatPublishedAt(iso: string | Date | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function VersionHistoryDialog({open, onOpenChange, groupSlug, canManage, dirty, onRestored}: VersionHistoryDialogProps) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);

  // Reset transient row state on close — keying off `open` covers every close path
  // (X, Escape, and the restore that closes the dialog itself) so a reopen starts fresh.
  useEffect(() => {
    if (!open) {
      setExpanded(null);
      setConfirming(null);
    }
  }, [open]);

  const {data, isLoading} = trpc.internal.enrollmentSite.listVersions.useQuery(
    {groupSlug},
    {enabled: open},
  );

  const versions = data?.versions ?? [];
  const currentVersion = data?.currentVersion ?? null;

  // Load the version into the editor as an unsaved change — the author reviews, then Save
  // keeps it, Discard backs out, and Publish rolls the live site back to it.
  function restore(v: {siteConfig: unknown; e123Config: unknown; version: number}) {
    onRestored({siteConfig: v.siteConfig, e123Config: v.e123Config, version: v.version});
    onOpenChange(false);
    toast({
      title: `Loaded v${v.version} into the editor`,
      description: 'Review it, then Publish to roll the live site back — or Discard to back out.',
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <History className='h-5 w-5' />
            Version history
          </DialogTitle>
          <DialogDescription>
            Every publish saves a version. Restore one to load it into the editor — Save to keep it,
            or Publish to roll the live site back to it.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='grid place-items-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : versions.length === 0 ? (
          <div className='rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground'>
            No published versions yet. Publishing this site saves its first version.
          </div>
        ) : (
          <ul className='max-h-[60vh] space-y-3 overflow-y-auto'>
            {versions.map((v, i) => {
              const isCurrent = v.version === currentVersion;
              const isOpen = expanded === v.version;
              // Diff against the next-older version to show what that publish changed.
              const older = versions[i + 1];
              const changes = older
                ? diffGroupConfigs(older.siteConfig as GroupConfig, v.siteConfig as GroupConfig)
                : [];
              const isConfirming = confirming === v.version;
              return (
                <li key={v.version} className='rounded-xl border bg-white'>
                  <div className='flex items-start justify-between gap-3 p-4'>
                    <button
                      type='button'
                      onClick={() => setExpanded(isOpen ? null : v.version)}
                      className='flex min-w-0 flex-1 items-start gap-2 text-left'
                    >
                      {isOpen ? (
                        <ChevronDown className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                      ) : (
                        <ChevronRight className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                      )}
                      <div className='min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='font-semibold'>v{v.version}</span>
                          {isCurrent && (
                            <Badge variant='success' className='font-normal'>
                              Live
                            </Badge>
                          )}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {formatPublishedAt(v.publishedAt)} · {v.publishedBy}
                        </div>
                      </div>
                    </button>
                    {!isCurrent &&
                      (isConfirming ? (
                        // Only reached with unsaved edits — confirm the overwrite in place.
                        <div className='flex shrink-0 items-center gap-2'>
                          <Button variant='ghost' size='sm' onClick={() => setConfirming(null)}>
                            Cancel
                          </Button>
                          <Button
                            size='sm'
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                            disabled={!canManage}
                            onClick={() => restore(v)}
                          >
                            <RotateCcw className='mr-1.5 h-4 w-4' />
                            Overwrite draft
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant='outline'
                          size='sm'
                          className='shrink-0'
                          disabled={!canManage}
                          onClick={() => (dirty ? setConfirming(v.version) : restore(v))}
                        >
                          <RotateCcw className='mr-1.5 h-4 w-4' />
                          Restore to draft
                        </Button>
                      ))}
                  </div>
                  {isOpen && (
                    <div className='border-t p-4'>
                      {older ? (
                        changes.length === 0 ? (
                          <p className='text-sm text-muted-foreground'>
                            No content changes from v{older.version}.
                          </p>
                        ) : (
                          <>
                            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                              Changed from v{older.version}
                            </p>
                            <ConfigDiffList changes={changes} scroll={false} />
                          </>
                        )
                      ) : (
                        <p className='text-sm text-muted-foreground'>First published version.</p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
